import { RealtimeRole } from '@doxyme/realtime';
import { useAppRouter } from '../useAppRouter';

import { getApolloSessionDetails } from '../../features/apolloSession/apolloSession.selectors';
import { getIdentityId } from '../../features/realtime/realtime.selectors';
import {
  track as _track,
  type TrackedEvents,
} from '../../features/tracking/track';
import { useAppSelector } from '../useReduxToolkit';
import { useSession } from '../useSession';
import { getEffectiveSubscriptionPlan } from '../useUserSubscription/helpers/getEffectiveSubscriptionPlan';
import { getUserPlanType } from '../useUserSubscription/helpers/getUserPlanType';
import { useUserSubscription } from '../useUserSubscription';
import { getGPUData } from '../../shared/deviceCapabilities/getGpuData';
import { getIsAnimationDisabled } from '../../features/uiState/uiState.selectors';
import { Vendor } from '@doxyme/types';

export type UseTrackReturnProps = (
  eventName: TrackedEvents,
  data: Record<string, unknown>,
) => Promise<void>;

export const useTrack = (): UseTrackReturnProps => {
  const { pathname } = useAppRouter();
  const apolloSession = useAppSelector(getApolloSessionDetails);
  const {
    vendor: apolloSessionVendor,
    role,
    roomId,
    sessionId,
    dbSessionId,
    institutionId,
  } = apolloSession;

  // TODO: clean up this expression as part of CS-13582 refactor.
  const isProvider =
    role === RealtimeRole.Provider &&
    !pathname.includes('check-in') &&
    !pathname.includes('waiting-room');

  const vendor = isProvider ? Vendor.Vonage : apolloSessionVendor;
  const session = useSession();
  const identityId = useAppSelector(getIdentityId);
  const isAnimationDisabled = useAppSelector(getIsAnimationDisabled);
  const { plan } = useUserSubscription();
  const planType = getUserPlanType(getEffectiveSubscriptionPlan(plan));

  const track = async (
    eventName: TrackedEvents,
    data: Record<string, unknown>,
  ): Promise<void> => {
    const { tier: gpuTier } = await getGPUData();
    return _track(eventName, {
      sessionId: dbSessionId,
      vendor,
      roomId,
      planType,
      userType: role,
      clinicId: institutionId,
      channel: session.sessionId ?? sessionId,
      personUuid: identityId,
      gpuTier,
      isAnimationDisabled,
      timestamp: new Date().toISOString(),
      ...data,
    });
  };

  return track;
};
